// ---------------------------------------------------------------------------
// Iter 62ch — Réglages page
// ---------------------------------------------------------------------------
// Central place for user-facing personal settings. Starts with the Calendly
// per-user connection (moved from MorePage), and will host future toggles
// (notifs, thème, préférences calendrier…).
// ---------------------------------------------------------------------------
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useIsMobile } from '../hooks/useIsMobile';
import { Calendar, ExternalLink, ChevronRight, Lightbulb, LayoutGrid, X, Plus, GripVertical } from '../components/icons';
import { DAY_VIEW_PREF_KEY, readDayViewPref, writeDayViewPref } from './TasksV2';
import {
  SIDEBAR_HIDEABLE, readHiddenSidebar, writeHiddenSidebar,
  readSidebarOrder, writeSidebarOrder,
  readSidebarCustoms, writeSidebarCustoms,
  getAllSidebarItems,
} from '../components/AppLayout';

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();

  return (
    <div
      data-testid="settings-page"
      style={{
        minHeight: '100dvh',
        background: 'var(--bg-primary)',
        color: 'var(--text-primary)',
        fontFamily: 'Lexend, -apple-system, sans-serif',
        paddingBottom: isMobile ? 96 : 40,
      }}
    >
      {/* Top bar */}
      {isMobile && (
        <div style={{
          padding: '18px 20px 12px',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <button
            data-testid="settings-back-btn"
            onClick={() => navigate(-1)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#5BBEFC',
              fontFamily: 'Lexend', fontSize: 15, fontWeight: 600,
              cursor: 'pointer', padding: 0,
              display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            <ChevronRight size={16} color="#5BBEFC" style={{ transform: 'rotate(180deg)' }} />
            Retour
          </button>
        </div>
      )}

      {/* Responsive shell : max 780px on desktop, centered */}
      <div
        style={{
          maxWidth: isMobile ? undefined : 780,
          margin: isMobile ? 0 : '0 auto',
          padding: isMobile ? '4px 20px 12px' : '28px 32px 12px',
        }}
      >
        <h1
          data-testid="settings-title"
          style={{
            margin: 0,
            fontFamily: 'Sora, -apple-system, sans-serif',
            fontWeight: 800,
            fontSize: isMobile ? 32 : 40,
            letterSpacing: '-0.02em',
            color: 'var(--t1)',
          }}
        >
          Réglages
        </h1>
        <p style={{
          margin: '6px 0 0',
          fontSize: isMobile ? 13 : 14,
          color: 'var(--text-dim)',
        }}>
          Personnalise ASA à ta manière · connecté en tant que <b style={{ color: 'var(--text-primary)' }}>{user?.first_name || user?.name || 'toi'}</b>
        </p>
      </div>

      {/* Sections */}
      <div
        style={{
          maxWidth: isMobile ? undefined : 780,
          margin: isMobile ? 0 : '0 auto',
          padding: isMobile ? '20px' : '24px 32px',
          display: 'flex', flexDirection: 'column', gap: isMobile ? 24 : 28,
        }}
      >
        <GoogleCalendarSection />
        <MyCalendlySection />
        <TasksCalendarPreferenceSection />
        <SidebarPreferencesSection />
        {user?.role === 'founder' && <SidebarEditableSection user={user} />}
        {user?.role === 'founder' && <ProjectDuplicatesSection />}
        {user?.role === 'founder' && <AsaClientSyncSection />}
        <WidgetTutorialSection />
        <ComingSoonSection />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Iter 62cv — "Éditables" : reorder complet de la sidebar + raccourcis custom.
// FOUNDER-ONLY (Rayan msg 2026-02-20 : "ça c'est que pour la vue admin").
// ---------------------------------------------------------------------------
function SidebarEditableSection({ user }) {
  const [order, setOrder] = useState(() => readSidebarOrder());
  const [customs, setCustoms] = useState(() => readSidebarCustoms());
  const [newLabel, setNewLabel] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [newEmoji, setNewEmoji] = useState('🔗');

  // Compute the full ordered list = order applied to all items + any missing appended
  const displayItems = useMemo(() => {
    const builtins = getAllSidebarItems(user);
    const builtinById = new Map(builtins.map((it) => [it._id, it]));
    const customById = new Map(customs.map((c) => [`custom:${c.id}`, {
      _id: `custom:${c.id}`, label: c.label, url: c.url, emoji: c.emoji || '🔗', _custom: true,
    }]));
    const seen = new Set();
    const out = [];
    for (const id of (order || [])) {
      if (seen.has(id)) continue;
      seen.add(id);
      if (builtinById.has(id)) out.push(builtinById.get(id));
      else if (customById.has(id)) out.push(customById.get(id));
    }
    for (const it of builtins) if (!seen.has(it._id)) out.push(it);
    for (const [id, c] of customById.entries()) if (!seen.has(id)) out.push(c);
    return out;
  }, [order, customs, user]);

  const persistOrder = (nextItems) => {
    const nextOrder = nextItems.map((it) => it._id);
    setOrder(nextOrder);
    writeSidebarOrder(nextOrder);
  };

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= displayItems.length) return;
    const next = [...displayItems];
    [next[idx], next[j]] = [next[j], next[idx]];
    persistOrder(next);
  };

  const addShortcut = (e) => {
    e.preventDefault();
    const label = newLabel.trim();
    let url = newUrl.trim();
    if (!label || !url) return;
    if (!/^https?:\/\//i.test(url)) url = 'https://' + url;
    const id = `${Date.now()}${Math.floor(Math.random() * 1000)}`;
    const nextCustoms = [...customs, { id, label, url, emoji: (newEmoji || '🔗').slice(0, 4) }];
    setCustoms(nextCustoms);
    writeSidebarCustoms(nextCustoms);
    // append the newcomer to the end of the persisted order so it doesn't jump elsewhere
    const nextOrder = [...(order || [])];
    if (!nextOrder.includes(`custom:${id}`)) nextOrder.push(`custom:${id}`);
    setOrder(nextOrder);
    writeSidebarOrder(nextOrder);
    setNewLabel(''); setNewUrl(''); setNewEmoji('🔗');
    toast.success('Raccourci ajouté');
  };

  const removeCustom = (customId) => {
    const nextCustoms = customs.filter((c) => c.id !== customId);
    setCustoms(nextCustoms);
    writeSidebarCustoms(nextCustoms);
    const nextOrder = (order || []).filter((id) => id !== `custom:${customId}`);
    setOrder(nextOrder);
    writeSidebarOrder(nextOrder);
    toast.success('Raccourci supprimé');
  };

  const resetOrder = () => {
    if (!window.confirm('Remettre la barre latérale par défaut ? (les raccourcis custom sont conservés)')) return;
    setOrder([]);
    writeSidebarOrder([]);
    toast.success('Ordre réinitialisé');
  };

  return (
    <SectionCard
      title="Éditables · ordre & raccourcis"
      subtitle="Réorganise ta barre latérale et ajoute tes propres raccourcis (Notion, Docs, etc.) — vue Founder"
    >
      {/* Reorderable list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
        {displayItems.map((it, idx) => {
          const isCustom = !!it._custom;
          return (
            <div
              key={it._id}
              data-testid={`sidebar-edit-row-${it._id}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 4px',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
              }}
            >
              <GripVertical size={12} color="var(--t4)" style={{ flexShrink: 0 }} />
              <span
                aria-hidden
                style={{
                  width: 28, height: 28, borderRadius: 7,
                  display: 'grid', placeItems: 'center',
                  background: isCustom ? 'rgba(245,197,106,0.15)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${isCustom ? 'rgba(245,197,106,0.30)' : 'rgba(255,255,255,0.08)'}`,
                  fontSize: 13, flexShrink: 0,
                }}
              >{isCustom ? it.emoji : <LayoutGrid size={13} color="var(--t2)" />}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 13.5,
                  color: 'var(--t1)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{it.label}</div>
                <div style={{
                  fontFamily: 'Sora', fontWeight: 600, fontSize: 10.5,
                  color: 'var(--t3)', marginTop: 1,
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                }}>{isCustom ? 'Raccourci custom' : (it._section || 'Built-in')}</div>
              </div>
              <button
                data-testid={`sidebar-edit-up-${it._id}`}
                onClick={() => move(idx, -1)}
                disabled={idx === 0}
                aria-label="Monter"
                style={{
                  width: 26, height: 26, borderRadius: 6,
                  border: '1px solid var(--bd)', background: 'var(--sf)',
                  color: 'var(--t2)', cursor: idx === 0 ? 'default' : 'pointer',
                  opacity: idx === 0 ? 0.35 : 1,
                  display: 'grid', placeItems: 'center',
                }}
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M6 15L12 9L18 15"/></svg>
              </button>
              <button
                data-testid={`sidebar-edit-down-${it._id}`}
                onClick={() => move(idx, +1)}
                disabled={idx === displayItems.length - 1}
                aria-label="Descendre"
                style={{
                  width: 26, height: 26, borderRadius: 6,
                  border: '1px solid var(--bd)', background: 'var(--sf)',
                  color: 'var(--t2)', cursor: idx === displayItems.length - 1 ? 'default' : 'pointer',
                  opacity: idx === displayItems.length - 1 ? 0.35 : 1,
                  display: 'grid', placeItems: 'center',
                }}
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M6 9L12 15L18 9"/></svg>
              </button>
              {isCustom && (
                <button
                  data-testid={`sidebar-edit-delete-${it._id}`}
                  onClick={() => removeCustom(it._id.replace('custom:', ''))}
                  aria-label="Supprimer ce raccourci"
                  style={{
                    width: 26, height: 26, borderRadius: 6,
                    border: '1px solid rgba(255,107,107,0.30)',
                    background: 'rgba(255,107,107,0.10)',
                    color: '#FF6B6B', cursor: 'pointer',
                    display: 'grid', placeItems: 'center',
                  }}
                >
                  <X size={13} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Add shortcut form */}
      <form
        onSubmit={addShortcut}
        style={{
          padding: 12, borderRadius: 12,
          background: 'var(--sf2)',
          border: '1px dashed var(--bd)',
          display: 'flex', gap: 8, alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <input
          data-testid="sidebar-new-emoji"
          value={newEmoji}
          onChange={(e) => setNewEmoji(e.target.value)}
          maxLength={4}
          placeholder="🔗"
          style={{
            width: 46, textAlign: 'center',
            background: 'var(--sf)', border: '1px solid var(--bd)',
            borderRadius: 8, padding: '9px 6px',
            color: 'var(--t1)', fontSize: 15,
            outline: 'none',
          }}
        />
        <input
          data-testid="sidebar-new-label"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder="Nom du raccourci (ex: Notion)"
          style={{
            flex: '1 1 160px',
            background: 'var(--sf)', border: '1px solid var(--bd)',
            borderRadius: 8, padding: '9px 12px',
            color: 'var(--t1)', fontFamily: 'Sora', fontSize: 13,
            outline: 'none',
          }}
        />
        <input
          data-testid="sidebar-new-url"
          type="url"
          value={newUrl}
          onChange={(e) => setNewUrl(e.target.value)}
          placeholder="https://notion.so/..."
          style={{
            flex: '2 1 220px',
            background: 'var(--sf)', border: '1px solid var(--bd)',
            borderRadius: 8, padding: '9px 12px',
            color: 'var(--t1)', fontFamily: 'Sora', fontSize: 13,
            outline: 'none',
          }}
        />
        <button
          data-testid="sidebar-new-add"
          type="submit"
          disabled={!newLabel.trim() || !newUrl.trim()}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '9px 14px', borderRadius: 8,
            border: '1px solid #F5C56A',
            background: 'linear-gradient(140deg, rgba(245,197,106,0.25), rgba(245,197,106,0.10))',
            color: '#F5C56A',
            fontFamily: 'Sora', fontWeight: 800, fontSize: 12.5,
            cursor: (!newLabel.trim() || !newUrl.trim()) ? 'not-allowed' : 'pointer',
            opacity: (!newLabel.trim() || !newUrl.trim()) ? 0.5 : 1,
          }}
        >
          <Plus size={12} /> Ajouter
        </button>
      </form>

      {(order && order.length > 0) && (
        <button
          data-testid="sidebar-order-reset"
          onClick={resetOrder}
          style={{
            marginTop: 12,
            padding: '7px 12px', borderRadius: 8,
            border: '1px solid var(--bd)', background: 'var(--sf)',
            color: 'var(--t3)', fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
            cursor: 'pointer',
          }}
        >Réinitialiser l&apos;ordre par défaut</button>
      )}
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Iter 62cu — Sidebar visibility per user (Rayan msg 2026-02-20 : rendre optionnels
// Sales Elevate / Elevate School / Roadmap / Objectifs / Clients / ASA Script).
// Stored in localStorage, live-synced with the sidebar via a window event.
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Iter 62fg — Préférence vue jour (Tâches → Calendrier)
// Permet de choisir ce qu'il se passe quand on clique sur un jour :
// toujours Kanban, toujours Journalière, ou toujours demander (défaut).
// ---------------------------------------------------------------------------
function TasksCalendarPreferenceSection() {
  const [pref, setPref] = useState(() => readDayViewPref());
  const options = [
    { id: 'ask',     label: 'Toujours me demander', hint: 'Un modal te propose Kanban ou Journalière à chaque clic' },
    { id: 'kanban',  label: 'Toujours Kanban',      hint: 'Clic sur un jour → va direct au Kanban de ce jour' },
    { id: 'horaire', label: 'Toujours Journalière', hint: 'Clic sur un jour → affiche la timeline horaire du jour' },
  ];
  const pick = (id) => {
    setPref(id);
    writeDayViewPref(id);
  };
  return (
    <SectionCard
      title="Tâches · Clic sur un jour"
      subtitle="Choisis ce qu'il se passe quand tu cliques sur un jour depuis le Calendrier"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {options.map((opt) => {
          const active = pref === opt.id;
          return (
            <label
              key={opt.id}
              data-testid={`tasks-day-view-pref-${opt.id}`}
              onClick={() => pick(opt.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '14px 4px',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                cursor: 'pointer',
              }}
            >
              <div style={{
                width: 22, height: 22, borderRadius: '50%',
                border: `2px solid ${active ? '#5BBEFC' : 'rgba(255,255,255,0.22)'}`,
                background: active ? 'rgba(91,190,252,0.14)' : 'transparent',
                display: 'grid', placeItems: 'center', flexShrink: 0,
                transition: 'all 0.14s var(--ease-out)',
              }}>
                {active && <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#5BBEFC', boxShadow: '0 0 10px rgba(91,190,252,0.6)' }} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 14,
                  color: 'var(--t1)',
                }}>{opt.label}</div>
                <div style={{
                  fontFamily: 'Sora', fontWeight: 500, fontSize: 11.5,
                  color: 'var(--t3)', marginTop: 2, letterSpacing: '-0.005em',
                }}>{opt.hint}</div>
              </div>
            </label>
          );
        })}
      </div>
      <div style={{
        marginTop: 12,
        fontFamily: 'Sora', fontWeight: 600, fontSize: 11.5,
        color: 'var(--t4)', fontStyle: 'italic',
      }}>
        Tu peux toujours switcher d'une vue à l'autre via le bouton inline dans l'en-tête des Tâches.
      </div>
    </SectionCard>
  );
}

function SidebarPreferencesSection() {
  const [hiddenSet, setHiddenSet] = useState(() => readHiddenSidebar());
  const toggle = (key) => {
    const next = new Set(hiddenSet);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setHiddenSet(next);
    writeHiddenSidebar(next);
  };
  return (
    <SectionCard
      title="Barre latérale"
      subtitle="Masque les onglets que tu n'utilises pas — ta config reste privée à ton compte"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {SIDEBAR_HIDEABLE.map((item) => {
          const visible = !hiddenSet.has(item.key);
          return (
            <label
              key={item.key}
              data-testid={`sidebar-pref-${item.key}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '14px 4px',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                cursor: 'pointer',
              }}
            >
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                display: 'grid', placeItems: 'center',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                flexShrink: 0,
              }}>
                <LayoutGrid size={14} color="var(--t2)" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 14,
                  color: 'var(--t1)',
                }}>{item.label}</div>
                <div style={{
                  fontFamily: 'Sora', fontWeight: 600, fontSize: 11,
                  color: 'var(--t3)', marginTop: 2,
                }}>
                  {item.defaultHidden ? 'Masqué par défaut' : 'Visible par défaut'}
                </div>
              </div>
              {/* iOS switch */}
              <button
                data-testid={`sidebar-pref-toggle-${item.key}`}
                type="button"
                role="switch"
                aria-checked={visible}
                onClick={(e) => { e.preventDefault(); toggle(item.key); }}
                style={{
                  width: 44, height: 26, borderRadius: 999,
                  background: visible ? '#F5C56A' : 'rgba(255,255,255,0.12)',
                  border: 'none', cursor: 'pointer', padding: 2,
                  position: 'relative',
                  transition: 'background 0.15s var(--ease-out)',
                  flexShrink: 0,
                }}
              >
                <span style={{
                  display: 'block',
                  width: 22, height: 22, borderRadius: '50%',
                  background: '#fff',
                  transform: `translateX(${visible ? 18 : 0}px)`,
                  transition: 'transform 0.18s var(--ease-out)',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                }} />
              </button>
            </label>
          );
        })}
      </div>
      <div style={{
        marginTop: 12,
        fontFamily: 'Sora', fontWeight: 600, fontSize: 11.5,
        color: 'var(--t4)', fontStyle: 'italic',
      }}>
        Ces onglets restent accessibles via URL directe même quand ils sont masqués.
      </div>
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Réglages → Section Calendly (Iter 62ci — OAuth "Se connecter avec Calendly")
// ---------------------------------------------------------------------------
function GoogleCalendarSection() {
  const [state, setState] = useState({ loading: true, connected: false });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/gcal/status');
      setState({ ...r.data, loading: false });
    } catch {
      setState({ connected: false, loading: false });
    }
  };
  useEffect(() => {
    load();
    const status = new URLSearchParams(window.location.search).get('gcal');
    if (status === 'ok') toast.success('Google Calendar connecté ✓ — tes tâches arrivent');
    if (status === 'error') toast.error('Connexion Google Calendar échouée');
    if (status) window.history.replaceState({}, '', window.location.pathname);
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.get('/gcal/connect');
      window.location.href = r.data.auth_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossible de démarrer la connexion');
      setBusy(false);
    }
  };
  const syncNow = async () => {
    setBusy(true);
    try {
      const r = await api.post('/gcal/sync');
      if (r.data.errors) toast.error(`${r.data.errors} tâche(s) refusée(s) par Google${r.data.last_error ? ` — ${r.data.last_error.slice(0, 120)}` : ''}`);
      else if (!r.data.tasks) toast.info('Aucune tâche datée assignée à toi sur les 6 prochains mois — rien à envoyer');
      else toast.success(`Synchro OK · ${r.data.tasks} tâches dans l'agenda « ASA · Tâches » de Google (${r.data.created} ajoutées · ${r.data.updated} mises à jour)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de synchro');
    } finally {
      setBusy(false);
    }
  };
  const disconnect = async () => {
    if (!window.confirm("Déconnecter Google Calendar ? L'agenda « ASA · Tâches » sera supprimé de ton Google.")) return;
    try {
      await api.delete('/gcal/disconnect');
      toast.success('Google Calendar déconnecté');
      load();
    } catch {
      toast.error('Erreur');
    }
  };

  const lastSync = state.last_sync_at
    ? new Date(state.last_sync_at).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : null;
  const pill = (bg, color, label) => (
    <span style={{ padding: '4px 9px', borderRadius: 999, background: bg, color, fontFamily: 'Sora', fontWeight: 800, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', flexShrink: 0 }}>{label}</span>
  );
  const row = { display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderBottom: '1px solid var(--border-subtle)' };
  const icon = (
    <div style={{ width: 38, height: 38, borderRadius: 10, background: 'linear-gradient(135deg, #4285F4 0%, #1A73E8 100%)', display: 'grid', placeItems: 'center', flexShrink: 0, boxShadow: '0 4px 12px rgba(66,133,244,0.35)' }}>
      <Calendar size={18} color="#fff" strokeWidth={2.2} />
    </div>
  );

  return (
    <SectionCard title="Google Calendar" subtitle="ASA ⇄ Google Calendar & Google Tasks, dans les 2 sens">
      {state.loading ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-dim)', fontSize: 13 }}>Chargement…</div>
      ) : state.connected ? (
        <div data-testid="gcal-connected">
          <div style={row}>
            {icon}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div data-testid="gcal-email" style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 14, color: 'var(--t1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{state.email || 'Compte Google'}</div>
              <div data-testid="gcal-sync-info" style={{ fontSize: 11.5, color: 'var(--text-dim)', marginTop: 2 }}>
                Agenda « ASA · Tâches » · {state.events || 0} tâche{state.events > 1 ? 's' : ''} synchronisée{state.events > 1 ? 's' : ''}{lastSync ? ` · dernière synchro ${lastSync}` : ' · première synchro en cours…'}
              </div>
            </div>
            {pill('rgba(52,211,153,0.18)', '#34D399', 'Live')}
          </div>
          {state.needs_tasks_scope ? (
            <button data-testid="gcal-upgrade-btn" onClick={connect} disabled={busy} style={{ margin: '12px 16px 0', width: 'calc(100% - 32px)', padding: '10px 12px', borderRadius: 10, background: 'rgba(245,197,106,0.12)', border: '1px solid rgba(245,197,106,0.45)', color: '#F5C56A', cursor: 'pointer', fontFamily: 'Sora', fontWeight: 800, fontSize: 12, textAlign: 'left' }}>
              ⚡ Reconnecte ton Google une fois pour activer la synchro dans les 2 sens (tâches sans date + Google Tasks)
            </button>
          ) : (
            <div data-testid="gcal-twoway-info" style={{ padding: '10px 16px 0', fontSize: 11.5, color: 'var(--text-dim)' }}>
              ⇄ 2 sens actifs · {state.imported || 0} élément{state.imported > 1 ? 's' : ''} Google dans ASA · {state.undated || 0} tâche{state.undated > 1 ? 's' : ''} sans date dans Google Tasks
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, padding: '12px 16px' }}>
            <button data-testid="gcal-sync-now" onClick={syncNow} disabled={busy} style={{ flex: 1, padding: '9px 12px', borderRadius: 10, border: 'none', cursor: busy ? 'wait' : 'pointer', background: 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)', color: '#0B1330', fontFamily: 'Sora', fontWeight: 800, fontSize: 12 }}>{busy ? 'Synchro…' : 'Synchroniser maintenant'}</button>
            <button data-testid="gcal-disconnect" onClick={disconnect} style={{ padding: '9px 12px', borderRadius: 10, background: 'transparent', border: '1px solid rgba(255,107,107,0.35)', color: '#FF6B6B', cursor: 'pointer', fontFamily: 'Sora', fontWeight: 700, fontSize: 12 }}>Déconnecter</button>
          </div>
        </div>
      ) : (
        <div>
          <button data-testid="gcal-connect-btn" onClick={connect} disabled={busy} style={{ ...row, width: '100%', textAlign: 'left', background: 'transparent', border: 'none', borderBottom: '1px solid var(--border-subtle)', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1 }}>
            {icon}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 14, color: 'var(--t1)' }}>{state.needs_reconnect ? 'Reconnecter Google Calendar' : 'Connecter Google Calendar'}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-dim)', marginTop: 2 }}>{busy ? 'Redirection vers Google…' : state.needs_reconnect ? "L'accès Google a expiré, 1 clic pour relancer" : '1 clic — chacun connecte son propre agenda'}</div>
            </div>
            {state.needs_reconnect ? pill('rgba(255,107,107,0.18)', '#FF6B6B', 'Expiré') : pill('linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)', '#0B1330', 'Connecter')}
          </button>
          <div style={{ padding: '12px 16px 16px', fontSize: 11.5, color: 'var(--text-dim)', lineHeight: 1.55 }}>
            <b style={{ color: 'var(--text-primary)' }}>Ce qui va se passer :</b> synchro dans les 2 sens. Tes tâches ASA datées vont dans l'agenda « ASA · Tâches », celles sans date dans Google Tasks (liste « ASA »). Et tes événements Google (calls, RDV) + tes tâches Google reviennent dans tes Tâches ASA. Décalage, validation, suppression : tout se met à jour tout seul.
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function MyCalendlySection() {
  const [state, setState] = useState({ connected: false, loading: true });
  const [connecting, setConnecting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/calendly/my-connection');
      setState({ ...r.data, loading: false });
    } catch {
      setState({ connected: false, loading: false });
    }
  };
  useEffect(() => {
    load();
    // Iter 62ci — handle ?calendly=connected|error redirect from OAuth callback
    const params = new URLSearchParams(window.location.search);
    const status = params.get('calendly');
    if (status === 'connected') {
      toast.success('Calendly connecté ✓');
      // Clean the query so refresh doesn't re-toast
      window.history.replaceState({}, '', window.location.pathname);
    } else if (status === 'error') {
      const reason = params.get('reason') || 'unknown';
      toast.error(`Connexion Calendly échouée (${reason})`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const startOAuth = async () => {
    setConnecting(true);
    try {
      const r = await api.get('/calendly/oauth/start');
      // Full-page redirect (safer than popup for OAuth on iOS Safari)
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossible de démarrer la connexion Calendly');
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Déconnecter ton Calendly d'ASA ?")) return;
    try {
      await api.delete('/calendly/my-connection');
      toast.success('Calendly déconnecté');
      load();
    } catch {
      toast.error('Erreur');
    }
  };

  return (
    <SectionCard title="Calendly" subtitle="Affiche tes rendez-vous directement dans ASA">
      {state.loading ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-dim)', fontSize: 13 }}>
          Chargement…
        </div>
      ) : state.connected ? (
        <div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14,
            padding: '14px 16px',
            borderBottom: '1px solid var(--border-subtle)',
          }}>
            <div style={{
              width: 34, height: 34, borderRadius: 8,
              background: 'rgba(52,211,153,0.24)',
              display: 'grid', placeItems: 'center', flexShrink: 0,
            }}>
              <Calendar size={16} color="#34D399" strokeWidth={2} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: 'Sora', fontWeight: 700, fontSize: 14,
                color: 'var(--t1)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {state.calendly_user?.name || 'Compte Calendly'}
              </div>
              <div style={{
                fontSize: 11.5, color: 'var(--text-dim)',
                marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                Connecté · {state.calendly_user?.email || ''}
              </div>
            </div>
            <span style={{
              padding: '4px 9px', borderRadius: 999,
              background: 'rgba(52,211,153,0.18)',
              color: '#34D399',
              fontFamily: 'Sora', fontWeight: 800, fontSize: 10,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              flexShrink: 0,
            }}>Actif</span>
          </div>
          {state.calendly_user?.scheduling_url && (
            <a
              href={state.calendly_user.scheduling_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="calendly-open-scheduling"
              style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '12px 16px',
                textDecoration: 'none',
                borderBottom: '1px solid var(--border-subtle)',
              }}
            >
              <ExternalLink size={15} color="var(--text-dim)" />
              <span style={{
                flex: 1, fontSize: 13,
                color: 'var(--text-primary)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {state.calendly_user.scheduling_url.replace(/^https?:\/\//, '')}
              </span>
              <ChevronRight size={14} color="var(--text-dim)" />
            </a>
          )}
          <button
            data-testid="calendly-disconnect"
            onClick={disconnect}
            style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '12px 16px', width: '100%',
              textAlign: 'left', background: 'transparent',
              border: 'none', cursor: 'pointer',
            }}
          >
            <span style={{ fontSize: 13, color: '#FF6B6B' }}>Déconnecter Calendly</span>
          </button>
        </div>
      ) : (
        <div style={{ padding: '4px 0 0' }}>
          <button
            data-testid="calendly-connect-btn"
            onClick={startOAuth}
            disabled={connecting}
            style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '16px 16px', width: '100%',
              textAlign: 'left', background: 'transparent',
              border: 'none', cursor: connecting ? 'wait' : 'pointer',
              opacity: connecting ? 0.7 : 1,
              borderBottom: '1px solid var(--border-subtle)',
            }}
          >
            <div style={{
              width: 38, height: 38, borderRadius: 10,
              background: 'linear-gradient(135deg, #006BFF 0%, #0059D9 100%)',
              display: 'grid', placeItems: 'center', flexShrink: 0,
              boxShadow: '0 4px 12px rgba(0,107,255,0.35)',
            }}>
              <Calendar size={18} color="#fff" strokeWidth={2.2} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 14, color: 'var(--t1)' }}>
                Se connecter avec Calendly
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-dim)', marginTop: 2 }}>
                {connecting ? 'Redirection en cours…' : '1 clic — pas de token à copier'}
              </div>
            </div>
            <span
              data-testid="calendly-oauth-cta"
              style={{
                padding: '8px 14px', borderRadius: 999,
                background: connecting
                  ? 'var(--bg-surface2)'
                  : 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)',
                color: connecting ? 'var(--text-dim)' : '#0B1330',
                fontFamily: 'Sora', fontWeight: 800, fontSize: 11,
                flexShrink: 0,
                boxShadow: connecting ? 'none' : '0 6px 14px rgba(62,123,250,0.35)',
              }}
            >
              {connecting ? '…' : 'Connecter'}
            </span>
          </button>
          <div style={{
            padding: '12px 16px 16px',
            fontSize: 11.5, color: 'var(--text-dim)', lineHeight: 1.55,
          }}>
            <b style={{ color: 'var(--text-primary)' }}>Ce qui va se passer :</b> tu vas être redirigé sur Calendly pour autoriser ASA (lecture seule de tes events et profil). Une fois validé, tu reviens ici automatiquement, c&apos;est connecté à vie.
          </div>
        </div>
      )}
    </SectionCard>
  );
}



// ---------------------------------------------------------------------------
// Iter 62ft — Scan & merge des projets doublons (founder-only).
// Détecte les projets ayant un nom normalisé identique (ex: "PodMax" vs
// "podmax projet") et offre un bouton de merge pour fusionner leurs
// tâches/concepts/asa_tasks dans un seul projet.
// ---------------------------------------------------------------------------
function ProjectDuplicatesSection() {
  const [groups, setGroups] = useState(null);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(null);

  const scan = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/projects-similar');
      setGroups(r.data.duplicate_groups || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur scan');
    } finally {
      setLoading(false);
    }
  };

  const merge = async (sourceId, targetId, sourceName, targetName) => {
    const ok = window.confirm(
      `Fusionner "${sourceName}" DANS "${targetName}" ?\n\n` +
      `Toutes les tâches, concepts et asa_tasks de "${sourceName}" seront déplacés vers "${targetName}", puis "${sourceName}" sera supprimé.\n\nCette action est irréversible.`
    );
    if (!ok) return;
    setMerging(sourceId);
    try {
      const r = await api.post('/admin/merge-projects', { source_id: sourceId, target_id: targetId });
      const d = r.data || {};
      toast.success(`✓ "${d.merged_from}" fusionné dans "${d.merged_into}" (${d.moved_tasks || 0} tâches déplacées)`);
      await scan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur merge');
    } finally {
      setMerging(null);
    }
  };

  return (
    <SectionCard
      title="Projets doublons"
      subtitle="Détecte et fusionne les projets au nom similaire (ex: 'PodMax' vs 'podmax projet')"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <button
          data-testid="scan-duplicates-btn"
          onClick={scan}
          disabled={loading}
          style={{
            padding: '9px 14px', borderRadius: 10,
            background: loading ? 'var(--sf)' : 'linear-gradient(180deg, #5BBEFC 0%, #3E7BFA 100%)',
            color: loading ? 'var(--t3)' : '#0B1330',
            border: 'none', cursor: loading ? 'wait' : 'pointer',
            fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
          }}
        >{loading ? 'Scan en cours…' : '🔍 Scanner les doublons'}</button>
        {groups !== null && (
          <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 12, color: groups.length === 0 ? '#5CF2A5' : '#F5C56A' }}>
            {groups.length === 0 ? '✓ Aucun doublon' : `${groups.length} groupe${groups.length > 1 ? 's' : ''} de doublons détecté${groups.length > 1 ? 's' : ''}`}
          </div>
        )}
      </div>

      {groups && groups.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {groups.map((g) => (
            <div key={g.normalized_name} style={{
              padding: 12, borderRadius: 10,
              background: 'rgba(245,197,106,0.06)',
              border: '1px solid rgba(245,197,106,0.25)',
            }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 12, color: '#F5C56A', marginBottom: 8 }}>
                "{g.normalized_name}" · {g.projects.length} projets
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {g.projects.map((p) => (
                  <div key={p.id} style={{
                    padding: 10, borderRadius: 8,
                    background: 'var(--sf)', border: '1px solid var(--bd)',
                    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                  }}>
                    <div style={{ flex: '1 1 180px' }}>
                      <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: 'var(--t1)' }}>
                        {p.name}{p.archived ? ' · 📦 archivé' : ''}
                      </div>
                      <div style={{ fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)' }}>
                        client: {p.client || '—'} · id: {p.id?.slice(0, 10)}… · créé: {p.created_at?.slice(0, 10) || '?'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {g.projects.filter((o) => o.id !== p.id).map((other) => (
                        <button
                          key={other.id}
                          data-testid={`merge-${p.id}-into-${other.id}`}
                          onClick={() => merge(p.id, other.id, p.name, other.name)}
                          disabled={merging === p.id}
                          style={{
                            padding: '6px 10px', borderRadius: 7,
                            background: 'var(--sf2)', border: '1px solid var(--bd)',
                            color: 'var(--t2)', cursor: merging === p.id ? 'wait' : 'pointer',
                            fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
                          }}
                        >→ Fusionner dans "{other.name}"</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}



// ---------------------------------------------------------------------------
// Iter 62fu — Sync ASA Interne ↔ ASA Client (founder-only).
// Permet au founder de re-notifier ASA Client pour toutes les vidéos
// actuellement en "À poster" (backfill). Utile pour synchroniser l'état
// initial ou corriger un écart.
// ---------------------------------------------------------------------------
function AsaClientSyncSection() {
  const [testing, setTesting] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const testPing = async () => {
    setTesting(true);
    try {
      const r = await api.post('/sync/asa-client/test');
      const sc = r?.data?.status_code;
      if (sc >= 200 && sc < 300) {
        toast.success(`✓ ASA Client répond (HTTP ${sc})`);
      } else {
        toast.error(`ASA Client KO (HTTP ${sc || '?'})`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur ping');
    } finally {
      setTesting(false);
    }
  };

  const backfill = async () => {
    const ok = window.confirm(
      "Re-envoyer un webhook 'video.to_post' pour TOUTES les vidéos actuellement en 'À poster' ?\n\n" +
      "ASA Client doit faire un upsert pour ne pas créer de doublons. C'est safe à relancer plusieurs fois."
    );
    if (!ok) return;
    setBackfilling(true);
    setLastResult(null);
    try {
      const r = await api.post('/admin/backfill-to-post-webhooks');
      setLastResult(r.data);
      toast.success(`✓ Backfill terminé · ${r.data.sent}/${r.data.total} envoyés`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur backfill');
    } finally {
      setBackfilling(false);
    }
  };

  const backfillPosted = async () => {
    if (!window.confirm("Re-envoyer un webhook 'video.posted' pour TOUTES les vidéos déjà postées ?\n\nSafe à relancer (upsert côté ASA Client).")) return;
    setBackfilling(true);
    setLastResult(null);
    try {
      const r = await api.post('/admin/backfill-posted-webhooks');
      setLastResult(r.data);
      toast.success(`✓ Postées resynchronisées · ${r.data.sent}/${r.data.total} envoyées`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur backfill');
    } finally {
      setBackfilling(false);
    }
  };

  return (
    <SectionCard
      title="Sync ASA Client"
      subtitle="Teste la connexion & re-notifie ASA Client pour toutes les vidéos en « À poster »"
    >
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <button
          data-testid="test-asa-client-ping"
          onClick={testPing}
          disabled={testing}
          style={{
            padding: '9px 14px', borderRadius: 10,
            background: 'var(--sf)', border: '1px solid var(--bd)',
            color: 'var(--t1)', cursor: testing ? 'wait' : 'pointer',
            fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
          }}
        >{testing ? 'Test…' : '📡 Tester la connexion'}</button>

        <button
          data-testid="backfill-to-post"
          onClick={backfill}
          disabled={backfilling}
          style={{
            padding: '9px 14px', borderRadius: 10,
            background: backfilling ? 'var(--sf)' : 'linear-gradient(180deg, #5CF2A5 0%, #3EC98B 100%)',
            color: backfilling ? 'var(--t3)' : '#0B1330',
            border: 'none', cursor: backfilling ? 'wait' : 'pointer',
            fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
          }}
        >{backfilling ? 'Backfill en cours…' : '🔄 Resynchroniser toutes les « À poster »'}</button>

        <button
          data-testid="backfill-posted"
          onClick={backfillPosted}
          disabled={backfilling}
          style={{
            padding: '9px 14px', borderRadius: 10,
            background: backfilling ? 'var(--sf)' : 'linear-gradient(180deg, #F5C56A 0%, #D99A42 100%)',
            color: backfilling ? 'var(--t3)' : '#0B1330',
            border: 'none', cursor: backfilling ? 'wait' : 'pointer',
            fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
          }}
        >{backfilling ? 'Backfill en cours…' : '✓ Renvoyer les vidéos déjà postées'}</button>
      </div>

      {lastResult && (
        <div style={{
          padding: 10, borderRadius: 8,
          background: lastResult.failures > 0 ? 'rgba(255,138,91,0.08)' : 'rgba(92,242,165,0.08)',
          border: `1px solid ${lastResult.failures > 0 ? 'rgba(255,138,91,0.25)' : 'rgba(92,242,165,0.25)'}`,
          fontFamily: 'Sora', fontWeight: 600, fontSize: 11.5, color: 'var(--t2)',
        }}>
          Total : <b>{lastResult.total}</b> · Envoyés : <b style={{ color: '#5CF2A5' }}>{lastResult.sent}</b> · Échecs : <b style={{ color: lastResult.failures > 0 ? '#FF8A5B' : 'var(--t3)' }}>{lastResult.failures}</b>
        </div>
      )}
    </SectionCard>
  );
}


// ---------------------------------------------------------------------------
// Réglages → Section Widget iOS (tutorial + open button)
// ---------------------------------------------------------------------------
function WidgetTutorialSection() {
  const [open, setOpen] = useState(false);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const openWidget = () => {
    window.open('/widget/calendar', '_blank', 'noopener,noreferrer');
  };
  return (
    <SectionCard
      title="Widget Calendrier"
      subtitle="Accès rapide à ton planning depuis l'écran d'accueil iPhone"
    >
      <div style={{ padding: '4px 0 0' }}>
        <button
          data-testid="widget-preview-btn"
          onClick={openWidget}
          style={{
            display: 'flex', alignItems: 'center', gap: 14,
            padding: '16px 16px', width: '100%',
            textAlign: 'left', background: 'transparent',
            border: 'none', borderBottom: '1px solid var(--border-subtle)',
            cursor: 'pointer',
          }}
        >
          <div style={{
            width: 38, height: 38, borderRadius: 10,
            background: 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)',
            display: 'grid', placeItems: 'center', flexShrink: 0,
            boxShadow: '0 4px 12px rgba(62,123,250,0.35)',
          }}>
            <Calendar size={18} color="#fff" strokeWidth={2.2} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 14, color: 'var(--t1)' }}>
              Ouvrir le widget maintenant
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-dim)', marginTop: 2 }}>
              Version fullscreen sans navbar
            </div>
          </div>
          <ExternalLink size={16} color="var(--text-dim)" />
        </button>

        <button
          data-testid="widget-tutorial-toggle"
          onClick={() => setOpen((v) => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: 14,
            padding: '14px 16px', width: '100%',
            textAlign: 'left', background: 'transparent',
            border: 'none', cursor: 'pointer',
          }}
        >
          <div style={{
            width: 34, height: 34, borderRadius: 8,
            background: 'rgba(91,190,252,0.14)',
            display: 'grid', placeItems: 'center', flexShrink: 0,
          }}>
            <Lightbulb size={16} color="#5BBEFC" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13.5, color: 'var(--t1)' }}>
              Comment installer le widget sur ton iPhone
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-dim)', marginTop: 2 }}>
              {open ? 'Masquer le guide' : 'Guide en 4 étapes'}
            </div>
          </div>
          <ChevronRight
            size={16}
            color="var(--text-dim)"
            style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}
          />
        </button>

        {open && (
          <div
            data-testid="widget-tutorial-content"
            style={{
              padding: '4px 16px 18px',
              display: 'flex', flexDirection: 'column', gap: 12,
              fontSize: 12.5, lineHeight: 1.55,
              color: 'var(--text-primary)',
            }}
          >
            {!isIOS && (
              <div style={{
                padding: '10px 12px',
                background: 'rgba(255,193,7,0.10)',
                border: '1px solid rgba(255,193,7,0.28)',
                borderRadius: 10,
                fontSize: 11.5,
                color: '#FFC107',
              }}>
                ⓘ Ce guide est fait pour iPhone / iPad (Safari). Sur Android le principe est le même via Chrome → &quot;Ajouter à l&apos;écran d&apos;accueil&quot;.
              </div>
            )}

            <Step n={1} title="Ouvre ASA dans Safari">
              Sur ton iPhone, va sur <b>asa-interne.com</b> dans <b>Safari</b> (pas dans Chrome ni via un lien in-app comme Instagram/WhatsApp — l&apos;install ne marche que depuis Safari).
            </Step>

            <Step n={2} title="Appuie sur le bouton Partager">
              En bas de l&apos;écran Safari, touche l&apos;icône <b>Partager</b> (un carré avec une flèche vers le haut, au centre de la barre).
            </Step>

            <Step n={3} title="« Sur l'écran d'accueil »">
              Fais défiler le menu qui apparaît et touche <b>&quot;Sur l&apos;écran d&apos;accueil&quot;</b> (parfois écrit &quot;Add to Home Screen&quot;). Confirme le nom (ASA) puis <b>Ajouter</b>.
            </Step>

            <Step n={4} title="Widget Calendrier en un tap">
              L&apos;icône ASA apparaît sur ton écran d&apos;accueil. Touche-la, elle s&apos;ouvre en fullscreen (sans barre Safari). Depuis là, va sur <b>Réglages → Widget → Ouvrir le widget</b> et &quot;ajoute à l&apos;écran d&apos;accueil&quot; à nouveau pour avoir <b>Widget Calendrier</b> en accès direct.
            </Step>

            <div style={{
              marginTop: 4,
              padding: '10px 12px',
              background: 'rgba(52,211,153,0.10)',
              border: '1px solid rgba(52,211,153,0.28)',
              borderRadius: 10,
              fontSize: 11.5,
              color: '#34D399',
            }}>
              ✓ Astuce : une fois installé, tes notifications push arrivent aussi directement sur le lockscreen, comme une vraie app.
            </div>
          </div>
        )}
      </div>
    </SectionCard>
  );
}

function Step({ n, title, children }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <div style={{
        width: 26, height: 26, borderRadius: 999,
        background: 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)',
        color: '#0B1330',
        display: 'grid', placeItems: 'center',
        fontFamily: 'Sora', fontWeight: 900, fontSize: 12,
        flexShrink: 0,
        boxShadow: '0 3px 8px rgba(62,123,250,0.35)',
      }}>{n}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: 'var(--t1)', marginBottom: 2 }}>
          {title}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-primary)', opacity: 0.85 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function ComingSoonSection() {  return (
    <SectionCard title="À venir" subtitle="D'autres réglages perso arrivent bientôt">
      <div style={{
        padding: '18px 16px',
        display: 'flex', alignItems: 'center', gap: 12,
        color: 'var(--text-dim)',
        fontSize: 12.5,
      }}>
        <Lightbulb size={16} color="var(--text-dim)" />
        <span>Notifs · Thème · Timezone · Signature · Avatar…</span>
      </div>
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Shared card wrapper
// ---------------------------------------------------------------------------
function SectionCard({ title, subtitle, children }) {
  return (
    <section
      style={{
        background: 'var(--bg-surface1)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 6px 22px rgba(0,0,0,0.28)',
      }}
    >
      <div style={{
        padding: '14px 16px 8px',
        borderBottom: '1px solid var(--border-subtle)',
      }}>
        <div style={{
          fontFamily: 'Sora, -apple-system, sans-serif',
          fontWeight: 700, fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          color: 'var(--text-dim)',
        }}>{title}</div>
        {subtitle && (
          <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-dim)' }}>
            {subtitle}
          </div>
        )}
      </div>
      {children}
    </section>
  );
}
